import type { IDefinition } from "tripetto-runner-foundation";

import type { ApplyFormYup, ApplyStep, ApplyStepYup } from "features/program-applications/utils";
import { hiddenColumns } from "lib/forms/hidden-columns";
import { PageServerParams as CohortsIndexPageParams } from "pages/programs/[programSlug]/cohorts/[cohortSlug]";
import { Application, ApplicationData, ApplicationStatusEnum } from "types/application.type";

export enum ApplicationStateEnum {
  NOT_STARTED,
  IN_PROGRESS,
  COMPLETED,
}

export const isApplicationComplete = (
  applicationSteps: CohortsIndexPageParams["applySteps"],
  applicationData?: ApplicationData,
) => {
  let isComplete = true;
  let firstUncompletedStep: (ApplyStepYup & { form: ApplyFormYup }) | undefined = undefined;

  if (!applicationData) {
    return {
      isComplete: false,
      firstUncompletedStep,
    };
  }
  // sort applySteps by index (asc)
  const applyStepsSortedByIndex = applicationSteps.sort((a: ApplyStep, b: ApplyStep) => {
    if (a.index === b.index) return 0;
    return a.index > b.index ? 1 : -1;
  });
  for (const applyStep of applyStepsSortedByIndex) {
    const applicationState = getApplicationState(applyStep.form.formDefinition, applicationData);
    if (applicationState !== ApplicationStateEnum.COMPLETED) {
      isComplete = false;
      firstUncompletedStep = applyStep;
      break;
    }
  }

  return {
    isComplete,
    firstUncompletedStep,
  };
};

export const isApplicationSubmitted = (application: Application): boolean =>
  application.status === ApplicationStatusEnum.SUBMITTED;

export const getApplicationState = (
  config: ApplyFormYup["formDefinition"],
  applicationData: ApplicationData,
): ApplicationStateEnum => {
  // TODO (richard): parse this object properly with something like superjson
  if (typeof config === "string") {
    config = JSON.parse(config);
  }
  // @ts-expect-error: TODO (richard): Remove type case once all data is migrated to database
  const requiredKeys = getRequiredKeys(config);
  const missingKeys: string[] = [];
  let hasCompletedKey = false;

  requiredKeys
    .filter((key) => !hiddenColumns.includes(key))
    .forEach((key) => {
      if (!applicationData[key] && applicationData[key] !== 0) {
        missingKeys.push(key);
      }
      if (applicationData[key]) {
        hasCompletedKey = true;
      }
    });
  if (missingKeys.length === 0) {
    return ApplicationStateEnum.COMPLETED;
  } else if (hasCompletedKey) {
    return ApplicationStateEnum.IN_PROGRESS;
  }

  return ApplicationStateEnum.NOT_STARTED;
};

const getRequiredKeys = (config: IDefinition): string[] => {
  const requiredKeys: string[] = [];

  config.clusters.forEach((cluster) => {
    cluster.nodes?.forEach((node) => {
      node.slots?.forEach((slot) => {
        if (slot.alias && slot.required && node.block?.type !== "localStorage") {
          requiredKeys.push(slot.alias);
        }
      });
    });
  });

  return requiredKeys;
};
